import * as React from 'react';
import { match, MatchFunction } from "path-to-regexp";
import { RouteDefinitionMap, PageLoadFunction, ReducerMap, QueryParamsMap } from 'app/RouteDefinition';
import { pageNotFound, pageLoaded, pageLoadFailed, startLoadingPage } from 'app/shared/pageSystem/actions';
import { Store } from 'redux';
import { combineReducers } from 'redux';
import queryString from "query-string";
import { notification } from 'antd';

type Route = {
    name: string
    pattern: MatchFunction<object>,
    page: React.ComponentType<any>
    loadPage: PageLoadFunction,
    reducers: ReducerMap
}

type RouteMap = {
    [name: string]: Route|undefined
}

const routeMap : RouteMap = {};
const routeList : Route[] = [];

let storeRef: Store<{}> | null = null;
let systemReducersRef: ReducerMap | null = null;
let activeLoadToken = 0;
const duplicateNavigationCooldownMs = 100;
let lastNavigationKey: string | null = null;
let lastNavigationTimestamp = 0;

function initRoutes(routeDefinitions: RouteDefinitionMap) {
    routeList.length = 0;
    Object.keys(routeMap).forEach(name => delete routeMap[name]);

    Object.keys(routeDefinitions).forEach(name => {
        const def = routeDefinitions[name];
        const route = {
            name: name,
            pattern: match(def.path, { decode: decodeURIComponent }),
            page: def.page,
            loadPage: def.loadPage || function() {},
            reducers: {...def.reducers}
        };

        routeMap[name] = route;
        routeList.push(route)
    });
}

function matchPath(path: string) {
    return routeList.find(x => x.pattern(path));
}

function installReducers(store: Store<{}>, systemReducers: ReducerMap, route: Route) {
    const allReducers = {
        ... systemReducers,
        ... route.reducers
    };
    const newReducer = combineReducers(allReducers);
    store.replaceReducer(newReducer);
    store.dispatch({type: 'framework/reducersChanged'});
}

function isPromise(value: unknown): value is Promise<unknown> {
    return !!value && typeof (value as PromiseLike<unknown>).then === 'function';
}

export function initializeRouting(store: Store<{}>, systemReducers: ReducerMap, routeDefinitions: RouteDefinitionMap) {
    storeRef = store;
    systemReducersRef = { ...systemReducers };
    activeLoadToken = 0;
    initRoutes(routeDefinitions);
}

export function handleLocationChange(path: string, search: string) {
    if (!storeRef || !systemReducersRef) {
        throw new Error('Routing has not been initialized');
    }

    const navigationKey = `${path}${search}`;
    const now = Date.now();
    if (lastNavigationKey === navigationKey && (now - lastNavigationTimestamp) < duplicateNavigationCooldownMs) {
        return;
    }
    lastNavigationKey = navigationKey;
    lastNavigationTimestamp = now;

    const route = matchPath(path);
    if (!route) {
        storeRef.dispatch(pageNotFound());
        return;
    }

    const token = ++activeLoadToken;
    storeRef.dispatch(startLoadingPage(route.name));
    installReducers(storeRef, systemReducersRef, route);

    const qsMap : QueryParamsMap = queryString.parse(search || '') as QueryParamsMap;

    let loadResult: void | Promise<void>;
    try {
        loadResult = route.loadPage(storeRef.dispatch, qsMap);
    }
    catch (err) {
        if (token === activeLoadToken) {
            storeRef.dispatch(pageLoadFailed(route.name));
        }
        console.error('Failed to load page', err);
        return;
    }

    if (isPromise(loadResult)) {
        loadResult.then(() => {
            if (token === activeLoadToken) {
                storeRef!.dispatch(pageLoaded(route.name));
            }
        }).catch(err => {
            if (token === activeLoadToken) {
                storeRef!.dispatch(pageLoadFailed(route.name));
            }
            console.error('Failed to load page', err);
        });
    }
    else {
        storeRef.dispatch(pageLoaded(route.name));
    }
}

export function lookupRoute(name: string) {
    const page = routeMap[name];
    return page;
}